import { Card } from "../../ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { useChartColors } from "../../../hooks/useChartColors";
import CustomerLoader from "../../../components/loaders/CustomerLoader";
import { useWholesaleCustomerSegmentation } from "../hooks/useWholesaleCustomerSeg";

export function WholesaleCustomerSegmentation() {

  const { data: segments = [], isLoading } = useWholesaleCustomerSegmentation();
  const colors = useChartColors("wholesale");
  if (!colors) return null;

  const highestAOV = [...segments].sort((a, b) => b.avgOrderValue - a.avgOrderValue)[0];
  const highestFrequency = [...segments].sort((a, b) => b.purchaseFrequency - a.purchaseFrequency)[0];
  const highestPercentage = [...segments].sort((a, b) => b.percentage - a.percentage)[0];


  return (
    <>
      <SummaryHeader
        title="Customer Segmentation"
      />
      <Card>
        <Tabs defaultValue="chart">
          <TabsList>
            <TabsTrigger value="chart">Chart View</TabsTrigger>
            <TabsTrigger value="table">Table View</TabsTrigger>
          </TabsList>
          <TabsContent value="chart" className="space-y-4">
            {isLoading ? (
                <CustomerLoader variant="customer-chart" />
              ) : (
            <><div className="h-[300px]">

                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={segments}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--muted)" />
                      <XAxis
                        dataKey="segment"
                        tick={{ fill: "var(--chart-text)" }}
                        axisLine={{ stroke: "var(--muted)" }} />
                      <YAxis
                        tick={{ fill: "var(--chart-text)" }}
                        axisLine={{ stroke: "var(--muted)" }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "var(--background)",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius)",
                          color: "var(--chart-text)",
                          fontSize: "0.875rem",
                        }} />
                      <Legend />
                      <Bar
                        dataKey="count"
                        name="Customer Count"
                        radius={[4, 4, 0, 0]}
                        fill={colors[0]}
                      >
                        {segments.map((_entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={colors[index % colors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                </div>
                {highestAOV && highestFrequency && highestPercentage && (
  <div className="grid grid-cols-3 gap-4">
    <div className="bg-muted/30 p-4 rounded-lg">
      <div className="text-sm text-muted-foreground">Highest Value Segment</div>
      <div className="text-xl font-semibold mt-1">{highestAOV.segment}</div>
      <div className="text-sm text-muted-foreground mt-1">
        Avg. Order: ${highestAOV.avgOrderValue}
      </div>
    </div>

    <div className="bg-muted/30 p-4 rounded-lg">
      <div className="text-sm text-muted-foreground">Most Frequent Buyers</div>
      <div className="text-xl font-semibold mt-1">{highestFrequency.segment}</div>
      <div className="text-sm text-muted-foreground mt-1">
        Purchase Frequency: {highestFrequency.purchaseFrequency}/year
      </div>
    </div>

    <div className="bg-muted/30 p-4 rounded-lg">
      <div className="text-sm text-muted-foreground">Growth Opportunity</div>
      <div className="text-xl font-semibold mt-1">{highestPercentage.segment}</div>
      <div className="text-sm text-muted-foreground mt-1">
        {highestPercentage.percentage}% of customer base
      </div>
    </div>
  </div>
)}
</>
             )}
          </TabsContent>

          <TabsContent value="table">
            <div className="rounded-md border overflow-hidden">
              {isLoading ? (
                <CustomerLoader variant="segmentation-tbl" /> 
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Segment</TableHead>
                    <TableHead className="text-right">Customers</TableHead>
                    <TableHead className="text-right">Percentage</TableHead>
                    <TableHead className="text-right">
                      Avg. Order Value
                    </TableHead>
                    <TableHead className="text-right">
                      Purchase Frequency
                    </TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {segments.map((segment, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {segment.segment}
                      </TableCell>
                      <TableCell className="text-right">
                        {segment.count.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {segment.percentage}%
                      </TableCell>
                      <TableCell className="text-right">
                        ${segment.avgOrderValue}
                      </TableCell>
                      <TableCell className="text-right">
                        {segment.purchaseFrequency}/year
                      </TableCell>
                      <TableCell>
  {segment.status === "Loyal" && (
    <Badge className="bg-emerald-500 hover:bg-emerald-600">Loyal</Badge>
  )}
  {segment.status === "High Value" && (
    <Badge className="bg-blue-500 hover:bg-blue-600">High Value</Badge>
  )}
  {segment.status === "Potential" && (
    <Badge className="bg-indigo-500 hover:bg-indigo-600">Potential</Badge>
  )}
  {segment.status === "New" && (
    <Badge className="bg-purple-500 hover:bg-purple-600">New</Badge>
  )}
  {segment.status === "At Risk" && (
    <Badge className="bg-pink-500 hover:bg-pink-600">At Risk</Badge>
  )}
  {segment.status === "Dormant" && (
    <Badge className="bg-red-500 hover:bg-red-600">Dormant</Badge>
  )}
</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    </>
  );
}
